import { call, put, takeEvery, CallEffect, PutEffect, SelectEffect, select } from 'redux-saga/effects';

import { getImageLibrary, searchImageLibrary } from 'js/shared/providers/ImageLibrary';
import { FacetsList, ImageLibraryCategorySearch, ImageCategory, ImageLibraryInputSearch, RootState } from 'js/types';
import { showError } from 'js/actionCreators/uiActions';
import { MAX_IMAGE_SEARCH_INPUT_LENGTH } from 'js/editor/ImageDrawer/ImageLibrary';
import { FacetFilterConfig } from 'js/reducers/imageSearchReducer';
import { IMAGE_PROVIDER_TYPES } from 'js/config/consts';
import { SearchMode } from 'js/editor/ImageDrawer/imageSearchModeConfig';

import {
  GetCategoryImagesAction,
  GET_CATEGORY_IMAGES,
  getCategoryImagesSuccess,
  getCategoryImagesFailure,
  GET_SEARCH_IMAGES,
  GetSearchImagesAction,
  getSearchImagesSuccess,
  getSearchImagesFailure
} from '../actionCreators/ImageDrawerActions';

import { getImageCategories } from './selectors';

function* handleGetCategoryImages({
  categoryId,
  imageSource,
  nextPageId,
  pagination
}: GetCategoryImagesAction): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  ImageLibraryCategorySearch | Array<ImageCategory>
> {
  try {
    const imageCategories = (yield select(getImageCategories)) as Array<ImageCategory>;
    const category = imageCategories.find(category => category.id === categoryId);
    if (!category) throw new Error('Category not found');

    if (category.editorName === 'GIFs' && imageSource === 'noun-project') {
      yield put(getCategoryImagesSuccess(categoryId, imageSource, [], null));
      return;
    }

    const results = (yield call(
      getImageLibrary,
      categoryId,
      imageSource,
      nextPageId,
      pagination
    )) as ImageLibraryCategorySearch;
    yield put(
      getCategoryImagesSuccess(categoryId, imageSource, results.images, results.cursor ?? null, results.pagination)
    );
  } catch (err) {
    const error = err as Error;
    yield put(getCategoryImagesFailure(categoryId, imageSource));
    yield put(showError({ message: 'Failed getting images, please try again later', description: error.message }));
  }
}

export function* handleSearchImages({
  searchInput,
  imageSource,
  nextPageId,
  offset,
  isPrefetch = false,
  pagination
}: GetSearchImagesAction): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  ImageLibraryInputSearch | FacetFilterConfig | FacetsList | SearchMode | string[]
> {
  if (imageSource === 'streamline-graphics' && !searchInput) return;

  try {
    let selectedFilters: FacetFilterConfig | undefined;
    let activeFilterSources: string[] = [];
    let activeSearchMode: SearchMode = 'default';
    let characterSeedTerms: string[] = [];

    const isVideoScribeLibrarySearch = imageSource === 'videoscribe';

    if (isVideoScribeLibrarySearch) {
      activeSearchMode = (yield select((state: RootState) => state.imageSearch.activeSearchMode)) as SearchMode;
      characterSeedTerms = (yield select((state: RootState) => state.imageSearch.characterSeedTerms)) as string[];

      const visualStyleFilter = (yield select(
        (state: RootState) => state.imageSearch.visualStyleFilter
      )) as FacetFilterConfig;
      const regularFilters = (yield select(
        (state: RootState) => state.imageSearch.selectedFilters
      )) as FacetFilterConfig;
      const storedFacets = (yield select((state: RootState) => state.imageSearch.facets)) as FacetsList;

      selectedFilters = { ...visualStyleFilter, ...regularFilters };

      if (activeSearchMode === 'streamlineStyle') {
        // Streamline is a backend source, not a real facet — route to it and strip the key
        const strippedFilters = { ...selectedFilters };
        delete strippedFilters[IMAGE_PROVIDER_TYPES.STREAMLINE];
        selectedFilters = strippedFilters;
        activeFilterSources = [IMAGE_PROVIDER_TYPES.STREAMLINE];
      } else if (activeSearchMode === 'otherVisualStyle' || activeSearchMode === 'character') {
        activeFilterSources = [IMAGE_PROVIDER_TYPES.LIBRARY];
      } else {
        // Default mode: derive backend sources from facet metadata.
        // Fall back to the library source
        const activeFacetIds = Object.keys(selectedFilters).filter(id => selectedFilters![id].length > 0);
        if (activeFacetIds.length > 0) {
          const sourcesFromFacets = [
            ...new Set(storedFacets.filter(f => activeFacetIds.includes(f.id) && f.source).map(f => f.source!))
          ];
          activeFilterSources = sourcesFromFacets.length > 0 ? sourcesFromFacets : [IMAGE_PROVIDER_TYPES.LIBRARY];
        }
      }
    }

    const DEFAULT_EXTERNAL_SEARCH_TERM = 'people';
    let effectiveSearchInput: string;
    if (activeSearchMode === 'character' && characterSeedTerms.length > 0) {
      effectiveSearchInput = [...characterSeedTerms, searchInput].filter(Boolean).join(' ');
    } else {
      effectiveSearchInput =
        imageSource === 'noun-project' && !searchInput ? DEFAULT_EXTERNAL_SEARCH_TERM : searchInput;
    }
    const sanitizedSearchInput = effectiveSearchInput.substring(0, MAX_IMAGE_SEARCH_INPUT_LENGTH);
    const results = (yield call(
      searchImageLibrary,
      imageSource,
      sanitizedSearchInput,
      offset,
      nextPageId,
      selectedFilters,
      pagination,
      activeFilterSources
    )) as ImageLibraryInputSearch;
    const images = results.data.length ? results.data[0].images : [];

    let facets;
    if (isVideoScribeLibrarySearch) {
      facets = results.data[0].facets;
    }

    yield put(
      getSearchImagesSuccess({
        imageSource,
        images,
        offset: offset + results.meta.count, // don't use results.meta.offset, as it is not returning the correct value for noun-project searches
        total: results.meta.total,
        searchInput, // original (unsanitised) value used as reducer key; sanitisation is applied to the API call above
        isPrefetch,
        nextPageId: results.meta.next,
        facets,
        pagination: results.pagination
      })
    );
  } catch (err) {
    const error = err as Error;
    yield put(getSearchImagesFailure(imageSource, searchInput));
    yield put(showError({ message: 'Failed searching images, please try again later', description: error.message }));
  }
}

function* imageDrawerSagas() {
  yield takeEvery(GET_CATEGORY_IMAGES, handleGetCategoryImages);
  yield takeEvery(GET_SEARCH_IMAGES, handleSearchImages);
}

export default imageDrawerSagas;
